// src/components/MarketPricePanel.js
import { useState } from 'react';
import { api } from '../services/api';

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 13, height: 13, flexShrink: 0 }}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 11, height: 11, flexShrink: 0 }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

export default function MarketPricePanel({ productName }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [results, setResults] = useState([]);
  const [checkedAt, setCheckedAt] = useState(null);

  const check = async () => {
    if (!productName?.trim()) return;
    setState('loading');
    try {
      const data = await api.checkPrices(productName.trim());
      setResults(data.results || []);
      setCheckedAt(new Date());
      setState('done');
    } catch {
      setState('error');
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const diff = Math.round((Date.now() - date.getTime()) / 1000);
    if (diff < 10) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    return `${Math.round(diff / 60)}m ago`;
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      overflow: 'hidden',
      marginTop: 4,
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: 'var(--surface)',
        borderBottom: state === 'done' && results.length > 0 ? '1px solid var(--border)' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
          <TagIcon />
          <span>Market prices</span>
        </div>
        <button
          type="button"
          onClick={check}
          disabled={state === 'loading' || !productName?.trim()}
          style={{
            padding: '4px 10px',
            fontSize: '0.75rem',
            fontWeight: 500,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg)',
            color: state === 'loading' ? 'var(--text-muted)' : 'var(--text)',
            cursor: state === 'loading' || !productName?.trim() ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          {state === 'loading' ? (
            <>
              <span style={{ display: 'inline-block', width: 10, height: 10, border: '1.5px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Checking…
            </>
          ) : 'Check market prices'}
        </button>
      </div>

      {/* Results */}
      {state === 'done' && results.length > 0 && (
        <>
          <div style={{ background: 'var(--bg)' }}>
            {results.map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                {/* Thumbnail */}
                <div style={{
                  width: 36, height: 36, flexShrink: 0,
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {item.thumbnail
                    ? <img src={item.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 16 }}>🛍️</span>
                  }
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.platform}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                    {item.title}
                  </div>
                </div>

                {/* Price + link */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)' }}>
                    {item.price ?? '—'}
                  </span>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                      onClick={e => e.stopPropagation()}>
                      <ExternalLinkIcon />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div style={{
            padding: '6px 12px',
            fontSize: '0.68rem',
            color: 'var(--text-muted)',
            background: 'var(--surface)',
            borderTop: '1px solid var(--border)',
          }}>
            Prices from Google Shopping · Updated {formatTime(checkedAt)}
          </div>
        </>
      )}

      {state === 'done' && results.length === 0 && (
        <div style={{ padding: '12px', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', background: 'var(--bg)' }}>
          No results found for this product.
        </div>
      )}

      {state === 'error' && (
        <div style={{ padding: '12px', fontSize: '0.78rem', color: 'var(--danger)', textAlign: 'center', background: 'var(--bg)' }}>
          Could not fetch prices. Check your connection and try again.
        </div>
      )}
    </div>
  );
}
